import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Download, FileText, Filter, CheckCircle, Clock, XCircle } from 'lucide-react';

export default function EmployeeFilesReport() {
    const dummyFilesData = [
        {
            id: 'LN-2024-1001',
            applicantName: 'Rahul Sharma',
            loanType: 'Home Loan',
            amount: '₹45,00,000',
            assignedDate: '12 Jan 2024',
            status: 'Approved',
        },
        {
            id: 'LN-2024-1005',
            applicantName: 'Priya Patel',
            loanType: 'Personal Loan',
            amount: '₹5,00,000',
            assignedDate: '15 Jan 2024',
            status: 'Processing',
        },
        {
            id: 'LN-2024-1012',
            applicantName: 'Amit Kumar',
            loanType: 'Business Loan',
            amount: '₹12,50,000',
            assignedDate: '18 Jan 2024',
            status: 'Document Pending',
        },
        {
            id: 'LN-2024-1018',
            applicantName: 'Sneha Gupta',
            loanType: 'Auto Loan',
            amount: '₹8,00,000',
            assignedDate: '20 Jan 2024',
            status: 'Rejected',
        },
        {
            id: 'LN-2024-1022',
            applicantName: 'Vikram Singh',
            loanType: 'Home Loan',
            amount: '₹35,00,000',
            assignedDate: '22 Jan 2024',
            status: 'Disbursed',
        },
    ];

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Approved':
            case 'Disbursed':
                return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{status}</Badge>;
            case 'Processing':
                return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{status}</Badge>;
            case 'Document Pending':
                return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{status}</Badge>;
            case 'Rejected':
                return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{status}</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
                        Assigned File Report
                    </h1>
                    <p className="text-muted-foreground mt-1">Overview of all files assigned to you</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline">
                        <Filter className="h-4 w-4 mr-2" />
                        Filter
                    </Button>
                    <Button variant="outline">
                        <Download className="h-4 w-4 mr-2" />
                        Export CSV
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <Card className="border border-border">
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-blue-100 text-blue-600 rounded-lg">
                                <FileText className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">Total Files</p>
                                <p className="text-2xl font-bold">186</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border border-border">
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-amber-100 text-amber-600 rounded-lg">
                                <Clock className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">In Progress</p>
                                <p className="text-2xl font-bold">32</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border border-border">
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-emerald-100 text-emerald-600 rounded-lg">
                                <CheckCircle className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">Approved</p>
                                <p className="text-2xl font-bold">143</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border border-border">
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-red-100 text-red-600 rounded-lg">
                                <XCircle className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">Rejected</p>
                                <p className="text-2xl font-bold">11</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card className="border border-border">
                <CardHeader>
                    <CardTitle>Recent Assigned Files</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Application ID</TableHead>
                                <TableHead>Applicant Name</TableHead>
                                <TableHead>Loan Type</TableHead>
                                <TableHead>Amount</TableHead>
                                <TableHead>Assigned Date</TableHead>
                                <TableHead>Status</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {dummyFilesData.map((file) => (
                                <TableRow key={file.id}>
                                    <TableCell className="font-medium text-blue-600">{file.id}</TableCell>
                                    <TableCell>{file.applicantName}</TableCell>
                                    <TableCell>{file.loanType}</TableCell>
                                    <TableCell className="font-medium">{file.amount}</TableCell>
                                    <TableCell>{file.assignedDate}</TableCell>
                                    <TableCell>{getStatusBadge(file.status)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
